import { useCallback, useMemo } from 'react';
import { useCopyLink } from '@frameio/shared/routing';
import { getProjectLink, openInNewTab } from '../../utils';
import { STANDARD_RESULT_SOURCE } from '../constants';
import {
  useQuickSearchModal,
  useSearchHistoryData,
  useTrackActionedOnResults,
} from '../contexts';
import type {
  TrackResultSource,
  TrackSearchFilter,
} from '../contexts/SearchAnalyticsContext/types';
import { useProjectResultFieldMatchesV2Fragment } from '../gql/generated/QuickSearchProjectResultV2';
import type { SearchMode } from '../types';
import { getMatchTypeTrackingValues } from '../utils';

type UseProjectActionsParams = {
  appliedFilterTypes: TrackSearchFilter;
  isAdvancedSearch?: boolean;
  projectId: string;
  resultPosition: number;
  resultSource?: TrackResultSource;
  searchMode?: SearchMode;
  searchQuery?: string;
  workspaceId?: string;
};

export const useProjectActions = ({
  appliedFilterTypes,
  isAdvancedSearch = false,
  projectId,
  resultPosition,
  resultSource = STANDARD_RESULT_SOURCE,
  searchMode,
  searchQuery,
  workspaceId,
}: UseProjectActionsParams) => {
  const trackActionedOnResults = useTrackActionedOnResults();
  const copyLink = useCopyLink();
  const { setSelectedProject, closeModal } = useQuickSearchModal();
  const { addRecentSearch } = useSearchHistoryData();

  const {
    data: { matches },
  } = useProjectResultFieldMatchesV2Fragment({
    from: { __typename: 'QuickSearchProjectResult', id: projectId },
  });

  const webSource = isAdvancedSearch ? 'FullScreen' : 'Modal';

  const matchType = useMemo(
    () => getMatchTypeTrackingValues(matches?.nodes ?? []),
    [matches?.nodes]
  );

  const recordSearchHistory = useCallback(() => {
    if (!searchMode || !searchQuery) return;

    addRecentSearch({
      query: searchQuery,
      mode: searchMode,
    });
  }, [addRecentSearch, searchQuery, searchMode]);

  const handleCopyLink = useCallback(() => {
    recordSearchHistory();
    trackActionedOnResults({
      action: 'CopyUrl',
      filterApplied: appliedFilterTypes,
      matchType,
      resultPosition,
      resultSource,
      resultType: 'Project',
      webSource,
    });
    copyLink({
      page: 'folder',
      projectId,
    });
  }, [
    appliedFilterTypes,
    copyLink,
    matchType,
    projectId,
    recordSearchHistory,
    resultPosition,
    resultSource,
    trackActionedOnResults,
    webSource,
  ]);

  const handleOpenProjectSettings = useCallback(() => {
    recordSearchHistory();
    trackActionedOnResults({
      action: 'OpenSettings',
      filterApplied: appliedFilterTypes,
      matchType,
      resultPosition,
      resultSource,
      resultType: 'Project',
      webSource,
    });
    setSelectedProject(projectId, workspaceId ?? '');
    closeModal();
  }, [
    appliedFilterTypes,
    closeModal,
    matchType,
    projectId,
    recordSearchHistory,
    resultPosition,
    resultSource,
    setSelectedProject,
    trackActionedOnResults,
    webSource,
    workspaceId,
  ]);

  const handleOpenInNewTab = useCallback(() => {
    const href = getProjectLink(projectId);
    recordSearchHistory();
    openInNewTab(href);
  }, [recordSearchHistory, projectId]);

  return {
    handleCopyLink,
    handleOpenProjectSettings,
    handleOpenInNewTab,
    matchType,
  };
};
